/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "../../contexts/notificationContext";
import { invalidateQueryKeys } from "./utils/invalidateQueries";
import { getMutationErrorMessage } from "./utils/mutationErrorMessage";

const resolveMessage = (messageOrResolver, payload) => {
  if (!messageOrResolver) {
    return null;
  }

  if (typeof messageOrResolver === "function") {
    return messageOrResolver(payload);
  }

  return messageOrResolver;
};

export const useOtMutation = ({
  mutationFn,
  invalidateKeys = [],
  removeKeys = [],
  successMessage,
  errorMessage,
  onSuccess,
  onError,
  onSettled,
  ...mutationOptions
}) => {
  const queryClient = useQueryClient();
  const showNotification = useNotificationContext();

  return useMutation({
    mutationFn,
    ...mutationOptions,
    onSuccess: async (data, variables, context) => {
      if (onSuccess) {
        await onSuccess(data, variables, context);
      }

      await invalidateQueryKeys(queryClient, invalidateKeys);

      removeKeys.forEach((key) => {
        queryClient.removeQueries({ queryKey: key });
      });

      const successToastMessage = resolveMessage(successMessage, {
        data,
        variables,
        context,
      });

      if (successToastMessage) {
        showNotification(successToastMessage, "success");
      }
    },
    onError: async (error, variables, context) => {
      const fallbackErrorMessage = getMutationErrorMessage(error);
      const errorToastMessage =
        resolveMessage(errorMessage, { error, variables, context }) ||
        fallbackErrorMessage;

      if (errorToastMessage) {
        showNotification(errorToastMessage, "error");
      }

      if (onError) {
        await onError(error, variables, context);
      }
    },
    onSettled: async (data, error, variables, context) => {
      if (onSettled) {
        await onSettled(data, error, variables, context);
      }
    },
  });
};
